import { Currency } from '../enum/currency'
import { SignInMethod } from '../enum/sign-in-method'
import { Deletable } from '../interface/deletable'
import { Factory } from '../interface/factory'
import { Identifiable } from '../interface/identifiable'
import { Modifiable } from '../interface/modifiable'
import { ExpiryReminder } from './expiry-reminder'
import { GracePeriod } from './grace-period'
import { SupportedPaymentMethod } from './payment-method'
import { parsePricingSnapshot, PricingSnapshot } from './pricing-snapshot'
import { Unlocks } from './unlocks'
import {
  objectSchema,
  parseEntity,
  rememberPayload,
} from '../../_utils/fcdc-v2'

const sessionPayload = objectSchema({})

export enum SessionState {
  Tentative = 'Tentative',
  Booked = 'Booked',
  Confirmed = 'Confirmed',
}

export enum ExtensionType {
  Initial = 'Initial',
  AwaitingPhoneVerification = 'AwaitingPhoneVerification',
  AwaitingEmailVerification = 'AwaitingEmailVerification',
  AwaitingExternalPayment = 'AwaitingExternalPayment',
  Default = 'Default',
}

export enum Origin {
  LockerScan = 'LockerScan',
  PaymentTerminal = 'PaymentTerminal',
  Booking = 'Booking',
}

export interface Lookup {
  type: 'Email' | 'Phone'
  id: string
  siteId: string
  sessionId: string
  address?: string
  number?: string
}

export interface BeneficiaryInvitedPhone {
  type: 'InvitedPhone'
  verificationCode: string
  phone: string
  sentAt: Date
}

export interface BeneficiaryInvitedEmail {
  type: 'InvitedEmail'
  verificationCode: string
  email: string
  sentAt: Date
}

export interface BeneficiaryAccepted {
  type: 'Accepted'
  userId: string
  invitedAt: Date
  acceptedAt: Date
}

export type Beneficiary =
  | BeneficiaryInvitedPhone
  | BeneficiaryInvitedEmail
  | BeneficiaryAccepted

export function parseBeneficiary(json: any): Beneficiary {
  if (!json || typeof json !== 'object') {
    return json
  }
  if (json.type === 'Accepted') {
    return {
      ...json,
      type: 'Accepted',
      userId: json.userId,
      invitedAt: new Date(json.invitedAt),
      acceptedAt: new Date(json.acceptedAt),
    }
  }
  if (json.type === 'InvitedEmail') {
    return {
      ...json,
      type: 'InvitedEmail',
      verificationCode: json.verificationCode,
      email: json.email,
      sentAt: new Date(json.sentAt),
    }
  }
  if (json.type === 'InvitedPhone') {
    return {
      ...json,
      type: 'InvitedPhone',
      verificationCode: json.verificationCode,
      phone: json.phone,
      sentAt: new Date(json.sentAt),
    }
  }
  return { ...json }
}

export interface PayOnDelivery {
  amount: number
}

export interface BookingPreview {
  startAllocatingAt: Date
  start: Date
  expiry: Date
}

export class Session implements Identifiable, Modifiable, Deletable {
  deleted: boolean = false
  status: SessionState
  origin: Origin

  id: string
  siteId: string
  lockerId: string
  countryCode: string
  customerId: string
  presenceId: string
  locationId: string
  columnId: string
  currency: Currency

  expiry: Date
  created: Date
  modified: Date

  allowedSignInMethods?: SignInMethod[]
  paymentInProgress?: InProgress
  terminatedBy?: string

  constructor(json: any) {
    this.status = json.status
    this.origin = json.origin

    this.id = json.id
    this.siteId = json.siteId
    this.lockerId = json.lockerId
    this.countryCode = json.countryCode
    this.customerId = json.customerId
    this.presenceId = json.presenceId
    this.locationId = json.locationId
    this.columnId = json.columnId
    this.currency = json.currency

    this.expiry = new Date(json.expiry)
    this.created = new Date(json.created)
    this.modified = new Date(json.modified)

    this.allowedSignInMethods = json.allowedSignInMethods || []
    this.paymentInProgress = json.paymentInProgress
      ? new InProgress(json.paymentInProgress)
      : undefined
    this.terminatedBy = json.terminatedBy ? json.terminatedBy : undefined
    rememberPayload(this, json)
  }

  public static getFactory(): Factory<Session> {
    return new (class implements Factory<Session> {
      make(json: any): Session {
        switch (json.status) {
          case SessionState.Tentative:
            return new SessionTentative(json)
          case SessionState.Booked:
            return new SessionBooked(json)
          case SessionState.Confirmed:
            return new SessionConfirmed(json)
          default:
            return parseEntity(sessionPayload, json ?? {}) as unknown as Session
        }
      }

      getTableName(): string {
        return 'sessions'
      }
    })()
  }

  static getUrl(siteId: string, lockerId?: string, sessionId?: string): string {
    return (
      '/sites' +
      siteId +
      (lockerId ? '/lockers/' + lockerId : '') +
      (sessionId ? '/sessions/' + sessionId : '/sessions')
    )
  }
}

export class SessionTentative extends Session {
  verificationCode?: string
  desiredExpiry?: Date
  userId?: string
  desiredPriceCategoryId?: string
  desiredStart?: Date
  desiredAddons?: DesiredAddons
  selectedAccessibleLocker?: boolean
  payOnDelivery?: PayOnDelivery
  pricingSnapshot?: PricingSnapshot

  constructor(json: any) {
    super(json)
    this.verificationCode = json.verificationCode
    this.desiredExpiry = json.desiredExpiry
      ? new Date(json.desiredExpiry)
      : undefined
    this.userId = json.userId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.desiredPriceCategoryId = json.desiredPriceCategoryId
    this.desiredStart = json.desiredStart
      ? new Date(json.desiredStart)
      : undefined
    this.desiredAddons = json.desiredAddons
      ? new DesiredAddons(json.desiredAddons)
      : undefined
    this.selectedAccessibleLocker = json.selectedAccessibleLocker
    this.payOnDelivery = json.payOnDelivery
      ? { ...json.payOnDelivery, amount: Number(json.payOnDelivery.amount) }
      : undefined
    this.pricingSnapshot = parsePricingSnapshot(json.pricingSnapshot)
  }
}

export class SessionBooked extends Session {
  userId: string
  paymentIds: string[]
  desiredPriceCategoryId: string
  startAllocatingAt: Date
  start: Date
  terminatedAt?: Date
  confirmedAt: Date
  desiredAddons?: DesiredAddons
  expiryReminder?: ExpiryReminder
  gracePeriod?: GracePeriod
  unlocks?: Unlocks
  pricingSnapshot?: PricingSnapshot

  constructor(json: any) {
    super(json)
    this.userId = json.userId
    this.paymentIds = json.paymentIds
    this.desiredPriceCategoryId = json.desiredPriceCategoryId
    this.startAllocatingAt = new Date(json.startAllocatingAt)
    this.start = new Date(json.start)
    this.terminatedAt = json.terminatedAt
      ? new Date(json.terminatedAt)
      : undefined
    this.confirmedAt = new Date(json.confirmedAt)
    this.desiredAddons = json.desiredAddons
      ? new DesiredAddons(json.desiredAddons)
      : undefined
    this.expiryReminder = json.expiryReminder
      ? new ExpiryReminder(json.expiryReminder)
      : undefined
    this.gracePeriod = json.gracePeriod
      ? new GracePeriod(json.gracePeriod)
      : undefined
    this.unlocks = json.unlocks
      ? Unlocks.getFactory().make(json.unlocks)
      : undefined
    this.pricingSnapshot = parsePricingSnapshot(json.pricingSnapshot)
  }
}

export class SessionConfirmed extends Session {
  paymentIds: string[]
  confirmedAt: Date
  userId: string
  desiredPriceCategoryId: string
  allocatedPriceCategoryId: string
  allocatedAt?: Date
  awaitingClosure: boolean
  start: Date
  terminatedAt?: Date
  expiryReminder?: ExpiryReminder
  gracePeriod?: GracePeriod
  unlocks?: Unlocks
  userSatisfaction?: number
  desiredAddons?: DesiredAddons
  beneficiaries: Beneficiary[]
  selectedAccessibleLocker?: boolean
  payOnDelivery?: PayOnDelivery
  terminalCardSummaries: TerminalCardSummary[]
  pricingSnapshot?: PricingSnapshot

  constructor(json: any) {
    super(json)
    this.userId = json.userId
    this.paymentIds = json.paymentIds
    this.confirmedAt = new Date(json.confirmedAt)
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.desiredPriceCategoryId = json.desiredPriceCategoryId
    this.allocatedPriceCategoryId = json.allocatedPriceCategoryId
    this.allocatedAt = json.allocatedAt ? new Date(json.allocatedAt) : undefined
    this.awaitingClosure = json.awaitingClosure || false
    this.start = new Date(json.start)
    this.terminatedAt = json.terminatedAt
      ? new Date(json.terminatedAt)
      : undefined
    this.expiryReminder = json.expiryReminder
      ? new ExpiryReminder(json.expiryReminder)
      : undefined
    this.gracePeriod = json.gracePeriod
      ? new GracePeriod(json.gracePeriod)
      : undefined
    this.unlocks = json.unlocks
      ? Unlocks.getFactory().make(json.unlocks)
      : undefined
    this.userSatisfaction = json.userSatisfaction
    this.desiredAddons = json.desiredAddons
      ? new DesiredAddons(json.desiredAddons)
      : undefined
    this.beneficiaries = (json.beneficiaries || []).map(parseBeneficiary)
    this.selectedAccessibleLocker = json.selectedAccessibleLocker
    this.payOnDelivery = json.payOnDelivery
      ? { ...json.payOnDelivery, amount: Number(json.payOnDelivery.amount) }
      : undefined
    this.terminalCardSummaries = json.terminalCardSummaries
      ? json.terminalCardSummaries.map(
          (summary: any) => new TerminalCardSummary(summary)
        )
      : []
    this.pricingSnapshot = parsePricingSnapshot(json.pricingSnapshot)
  }
}

export class InProgress {
  paymentId: string
  priceTotal: number
  supportedPaymentMethods: SupportedPaymentMethod[]
  terminalCardSummary?: TerminalCardSummary

  constructor(json: any) {
    this.paymentId = json.paymentId
    this.priceTotal = json.priceTotal
    this.supportedPaymentMethods = json.supportedPaymentMethods?.length
      ? json.supportedPaymentMethods.map((method: any) =>
          SupportedPaymentMethod.getFactory().make(method)
        )
      : []
    this.terminalCardSummary = json.terminalCardSummary
      ? new TerminalCardSummary(json.terminalCardSummary)
      : undefined
    rememberPayload(this, json)
  }
}

export class DesiredAddons {
  unlocks?: DesiredUnlocks
  expiryReminder: boolean

  constructor(json: any) {
    this.unlocks = json.unlocks ? new DesiredUnlocks(json.unlocks) : undefined
    this.expiryReminder = json.expiryReminder || false
    rememberPayload(this, json)
  }
}

export class DesiredUnlocks {
  type: 'Limited' | 'Unlimited'
  count?: number

  constructor(json: any) {
    this.type = json.type
    this.count = json.count
    rememberPayload(this, json)
  }
}

export enum RefundStrategyType {
  Full = 'Full',
  DepositionOnly = 'DepositionOnly',
}

export interface RefundStrategy {
  type: RefundStrategyType
  includeDeposition?: boolean
}

export class SessionTerminateRequest {
  action = 'AdminAttempt'
  doNotUnlock = true
  refundStrategy?: RefundStrategy
}

export class TerminalCardSummary {
  userId: string
  maskedPan: string
  unmaskedPan?: string
  paymentBrand?: string
  untokenisedCardSummary?: string

  constructor(json: any) {
    this.userId = json.userId
    this.maskedPan = json.maskedPan
    this.unmaskedPan = json.unmaskedPan ?? undefined
    this.paymentBrand = json.paymentBrand ?? undefined
    this.untokenisedCardSummary = json.untokenisedCardSummary ?? undefined
    rememberPayload(this, json)
  }
}
